import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LumanityProvider } from "@lumanity/ui";
import { getEnv } from "@/config/env";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { DocumentViewerProvider } from "@/contexts/DocumentViewerContext";
import { DocumentViewerSheet } from "@/components/DocumentViewerSheet";
import { BannerAlertProvider } from "@/contexts/BannerAlertContext";
import ProtectedRoute, { AdminRoute, ProjectOwnerRoute } from "@/components/ProtectedRoute";
import Login from "./pages/Login";
import Index from "./pages/Index";
import CreateProject from "./pages/CreateProject";
import EditProject from "./pages/EditProject";
import ProjectDetail from "./pages/ProjectDetail";
// import EvidenceBase from "./pages/EvidenceBase"; // Hidden: to restore uncomment this and the /evidence route below
import Admin from "./pages/Admin";
import AllProjects from "./pages/AllProjects";
import NotFound from "./pages/NotFound";
import TherapyAreas from "./pages/admin/TherapyAreas";
import Templates from "./pages/admin/Templates";
import Prompts from "./pages/admin/Prompts";
import EvidenceTypes from "./pages/admin/EvidenceTypes";
import SourceTypes from "./pages/admin/SourceTypes";
import UsersAdmin from "./pages/admin/Users";
import BroadcastAdmin from "./pages/admin/Broadcast";
import TemplateTypes from "./pages/admin/TemplateTypes";
import TemplateFormatTypes from "./pages/admin/TemplateFormatTypes";
import ApiConfigurations from "./pages/admin/ApiConfigurations";
const queryClient = new QueryClient();

const AdminDefaultRedirect = () => {
  const { user } = useAuth();
  if (user?.role === "Admin") return <Navigate to="users" replace />;
  return <Navigate to="therapy-areas" replace />;
};

// Bridges this app's own auth (Cognito, via AuthContext) into @lumanity/ui's
// LumanityProvider — only used while trying out @lumanity/ui/chat locally.
const LumanityUIBridge = ({ children }: { children: React.ReactNode }) => {
  const { getAccessToken } = useAuth();
  const apiBaseUrl = getEnv("VITE_API_BASE_URL") || "http://localhost:8000";
  return (
    <LumanityProvider
      config={{
        apiBaseUrl,
        // Chat routes live under /api on the backend, unlike apiBaseUrl's other
        // consumers — must be set explicitly or the chat client falls back to
        // apiBaseUrl bare and 404s.
        chatApiUrl: `${apiBaseUrl}/api`,
        getAccessToken,
      }}
      queryClient={queryClient}
    >
      {children}
    </LumanityProvider>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <LumanityUIBridge>
      <BannerAlertProvider>
        <DocumentViewerProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <DocumentViewerSheet />
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/" element={<ProtectedRoute><Index /></ProtectedRoute>} />
                <Route path="/projects" element={<ProtectedRoute><AllProjects /></ProtectedRoute>} />
                <Route path="/projects/new" element={<ProjectOwnerRoute><CreateProject /></ProjectOwnerRoute>} />
                <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
                <Route path="/projects/:id/edit" element={<ProjectOwnerRoute><EditProject /></ProjectOwnerRoute>} />
                {/* Hidden: to restore uncomment the EvidenceBase import above and this route */}
                {/* <Route path="/evidence" element={<ProtectedRoute><EvidenceBase /></ProtectedRoute>} /> */}
                <Route path="/admin" element={<ProjectOwnerRoute><Admin /></ProjectOwnerRoute>}>
                  <Route index element={<AdminDefaultRedirect />} />
                  <Route path="users" element={<AdminRoute><UsersAdmin /></AdminRoute>} />
                  <Route path="broadcast" element={<AdminRoute><BroadcastAdmin /></AdminRoute>} />
                  <Route path="therapy-areas" element={<TherapyAreas />} />
                  <Route path="evidence-types" element={<AdminRoute><EvidenceTypes /></AdminRoute>} />
                  <Route path="source-types" element={<SourceTypes />} />
                  <Route path="templates" element={<Templates />} />
                  <Route path="prompts" element={<Prompts />} />
                  <Route path="template-types" element={<AdminRoute><TemplateTypes /></AdminRoute>} />
                  <Route path="template-format-types" element={<AdminRoute><TemplateFormatTypes /></AdminRoute>} />
                  <Route path="api-configurations" element={<AdminRoute><ApiConfigurations /></AdminRoute>} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </TooltipProvider>
        </DocumentViewerProvider>
      </BannerAlertProvider>
      </LumanityUIBridge>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
